import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { t } from "ttag";
import { Button, ChevronLeft, IconButton } from "@coolremote/shared";
import HVACImg from "../../images/coolmaster.png";
import {
  DeviceRegistrationLocationState,
  isValidDeviceRegistrationState
} from "./deviceRegistrationLocationState";
import styles from "./DeviceRegistrationPublic.module.css";

/**
 * Step 3 of the public (pre-login) device registration flow — CM-168.
 *
 * Asks whether the technician is a new or an existing CoolAutomation user, once
 * the device has been validated in CM-616. The mockup for this screen was taken
 * from iocControl and is dark purple; product decided every screen in this flow
 * is light, so it's re-skinned here — white background, plum app bar, coral
 * buttons — matching the rest of the flow.
 */
const UserSelection: React.FC = () => {
  const history = useHistory<DeviceRegistrationLocationState | undefined>();
  const deviceState = history.location.state;
  const hasValidDeviceState = isValidDeviceRegistrationState(deviceState);

  // Reached without a validated device (direct URL, or a refresh that cleared
  // location.state) — send the technician back to start over.
  useEffect(() => {
    if (!hasValidDeviceState) {
      history.replace("/register-device");
    }
  }, [hasValidDeviceState, history]);

  if (!hasValidDeviceState) {
    return null;
  }

  const handleBack = () => {
    // Real browser back, not a fresh push — this is what lets DeviceRegistrationWizard
    // read the serial/PIN/location back out of location.state when it remounts.
    history.goBack();
  };

  const handleContinueAsNewUser = () => {
    history.push("/register-device/new-user", deviceState);
  };

  const handleContinueAsExistingUser = () => {
    history.push("/register-device/existing-user", deviceState);
  };

  return (
    <div className={styles.screen}>
      <div className={styles.appBar}>
        <IconButton
          className={styles.backButton}
          onClick={handleBack}
          aria-label={t`Back`}
        >
          <ChevronLeft width={20} height={20} />
        </IconButton>
        <span className={styles.appBarTitle}>{t`Connecting Device`}</span>
        <span />
      </div>

      <div className={styles.userSelectionContent}>
        <img
          src={HVACImg}
          alt={t`HVAC device`}
          className={styles.userSelectionDevice}
        />

        <div className={styles.userSelectionButtons}>
          <Button
            variant="contained"
            className={styles.userSelectionButton}
            onClick={handleContinueAsNewUser}
          >
            {t`Continue as a new user`}
          </Button>
          <Button
            variant="contained"
            className={styles.userSelectionButton}
            onClick={handleContinueAsExistingUser}
          >
            {t`Continue as an existing user`}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UserSelection;
